import { FC, useContext, useRef, useState } from 'react';

import {
  AddImageIcon,
  BucketIcon,
  Button,
  H3,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller } from 'react-hook-form';

import { ImageUploaderFieldMobile } from 'components';
import { useUploadFile, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';

import s from './EditPhoto.module.scss';
import { EditPhotoTooltip } from './EditPhotoTooltip';
import { IEditPhotoProps } from './interfases';
import { FILE_MAXIMUM_SIZE } from './utils';

export const EditPhoto: FC<IEditPhotoProps> = ({ control, disabled }) => {
  const { t } = useContext(LocalizationContext);
  const [isHovered, setIsHovered] = useState(false);
  const imgInput = useRef<HTMLInputElement>(null);

  const { uploadImage } = useUploadFile();
  const size = useWindowResize();

  return (
    <>
      <div className={s.header}>
        <H3 className={s.title}>{t('goods.edit.photo.title')}</H3>
        <Tooltip content={<EditPhotoTooltip />} />
      </div>
      <Controller
        name="image"
        control={control}
        render={({ field: { value, onChange } }) => {
          if (size.width > 960)
            return (
              <div className={cn(s.container, disabled && s.disabled)}>
                {value ? (
                  <div
                    style={{
                      backgroundImage: `url(${value})`,
                    }}
                    className={s['image-container']}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    {isHovered && (
                      <div className={s['image-controls']}>
                        <Button
                          variant="outline"
                          color={'red'}
                          className={s['delete-button']}
                          onClick={() => onChange('')}
                        >
                          <BucketIcon color="error" size="m" />
                        </Button>

                        <Button
                          onClick={() => imgInput.current?.click()}
                          fullWidth
                        >
                          {t('change')}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className={s['default-image']}
                    onClick={() => {
                      imgInput.current?.click();
                    }}
                  >
                    <AddImageIcon size="xxl" />
                    <span className={s.default_text}>
                      {t('goods.edit.photo.placeholder')}
                    </span>
                  </div>
                )}
                <input
                  ref={imgInput}
                  type="file"
                  accept=".png, .jpg"
                  className={s['file-input']}
                  onChange={async (event) => {
                    const file = event.target.files?.[0];

                    if (!file || file.size > FILE_MAXIMUM_SIZE) {
                      if (event.target) event.target.value = '';
                      return notification.info({
                        t,
                        type: 'error',
                        title: t('upload.file.error'),
                        message: t('upload.file.error_2'),
                      });
                    }

                    try {
                      const imageUrl = await uploadImage(file);
                      onChange(imageUrl);
                    } catch (error: unknown) {
                      if (error instanceof Error) {
                        notification.info({
                          t,
                          type: 'error',
                          title: t('upload.file.error'),
                          message: error?.message,
                        });
                      } else {
                        notification.info({
                          t,
                          type: 'error',
                          title: t('upload.file.error'),
                          message: String(error),
                        });
                      }
                    }
                  }}
                />
              </div>
            );
          return (
            <ImageUploaderFieldMobile
              value={value}
              onChange={onChange}
              title={t('upload.photo')}
              disabled={disabled}
            />
          );
        }}
      />
    </>
  );
};
